import { ApolloClient, ApolloLink, defaultDataIdFromObject, InMemoryCache } from '@apollo/client';
import { REST_GRAPHQL_CLIENTNAME } from '@famly/stat_apollo-client';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { isE2e } from '@famly/mf_ts-utils_is-e2e';
import { AUTOMATIC_TYPENAME_PREFIX } from '@famly/tail_apollo-rest-link';
import { createAuthLink } from './create-auth-link';
import { createErrorLink } from './create-error-link';
import { createHttpLink } from './create-http-link';
import { createRestLink } from './create-rest-link';
import { createRetryLink } from './create-retry-link';
import graphqlTypesToMergeInCache from './graphql-types-to-merge-in-apollo-cache.json';
// Read the debug flag directly from localStorage to avoid an Nx boundary violation
// (this graphql-library cannot depend on `@famly/vet_ts-utils_local-storage-config`).
// The flag is defined as `DebugLocalStorageFlags.APOLLO_DEV_ERROR_MESSAGES`.
const isApolloDevMessagesFlagToggled = () => {
    try {
        return Boolean(typeof localStorage !== 'undefined' && localStorage.getItem('famly.APOLLO_DEV_ERROR_MESSAGES') === 'true');
    }
    catch (_a) {
        return false;
    }
};
if (isE2e() || isApolloDevMessagesFlagToggled()) {
    // Lazy-load the dev-only Apollo helpers so they are split into a separate
    // chunk and excluded from the main production bundle.
    import('@apollo/client/dev').then(({ loadDevMessages, loadErrorMessages }) => {
        loadDevMessages();
        loadErrorMessages();
    });
}
const persistedClients = new Map();
const typePolicies = Object.fromEntries(graphqlTypesToMergeInCache.map(name => [name, { merge: true }]));
const typePoliciesWithMergeStrategies = {
    ...typePolicies,
    // since the apollo 3 update, we need to define unique identifiers on all fields
    // those can be combined identifiers, like database keys, too.
    // wherever those are missing so far and neither id, _id nor key are present,
    // the cache updates will fail.
    Child: {
        fields: {
            groups: {
                keyArgs: ['groupId', 'date'],
                merge: (_, incoming) => incoming,
            },
            states: {
                keyArgs: ['from', 'to'],
                merge: (_, incoming) => incoming,
            },
        },
    },
    // Permissions assigned to a role are incorrectly id'd by only the permission id on the backend.
    // By setting `keyFields` to `false` we tell Apollo _not_ to globally cache the role permission,
    // but to embed it inline within its parent Role. Without this, every `RolePermission` with the
    // same permission id (across different roles) collapses into a single cache entry — last write
    // wins — and every role ends up displaying the same `granted`/`locked` values.
    // See https://famlyapp.atlassian.net/browse/FBS-11626
    RolePermission: {
        keyFields: false,
    },
    // A `PlanBehavior.id` is a *global* behavior identifier (e.g. `CanSavePlan`, `ShowPlanWarnings`;
    // see `planBehaviorIds` in `@famly/inv_plan-behavior-ids`), so it repeats across every plan.
    // The `preview` and `create` plan operations return several `PricedPlan`s at once, so the same behavior id appears
    // under multiple plans with potentially different `payload`s. With default normalization those
    // collapse into a single `__typename:id` cache entry — last write wins — and every plan reads
    // back the same behavior payload. `keyFields: false` embeds each behavior inline within its
    // parent `PricedPlan` instead, restoring per-plan isolation.
    PlanBehavior: {
        keyFields: false,
    },
    // A `PayableSource` is identified by the pair (`id`, `kind`): the same `id` can refer to a
    // different entity depending on its `kind` (e.g. a `session` vs a `product`), so `id` alone is
    // not a unique key. Apollo's default normalization keys only on `__typename:id`, which would
    // collapse two sources that share an id but differ in kind into one cache entry — last write
    // wins — corrupting the `kind` that consumers read back. `keyFields: false` embeds each source
    // inline within its parent payable instead; it is only ever read off that parent, so this is
    // safe and avoids depending on `kind` always being co-selected with `id`.
    PayableSource: {
        keyFields: false,
    },
    // An `App.id` is a `StoreAppId` *enum* (e.g. `STAFFING`, `INVOICING`), not a per-object id, so it
    // repeats across the feature store. `GetFeatureCategories` selects `app { id title subtitle … }`
    // under four sibling category buckets (`increaseRevenue`, `saveTime`, `learn`, `engage`) at once,
    // so the same app id can appear under multiple parents — the exact `PlanBehavior` shape. With
    // default normalization those would collapse into one `__typename:id` entry. Today this is only
    // latent (the backend maps each app to a single category and the per-app copy is immutable per id,
    // so a duplicate would read back identical data — like the `PermissionLeaf` case), but it is safe
    // by backend invariant rather than by the cache. `keyFields: false` embeds each app inline within
    // its category, so future config that places an app in two categories with differing copy cannot
    // silently corrupt. `App` is only ever read off its parent category (no `cache.identify` lookups).
    App: {
        keyFields: false,
    },
};
export const createApolloClient = (APIHelper, onError, overridingApiKey, middleWare, additionalTypePolicies = {}) => {
    const apiKey = overridingApiKey || APIHelper.apiKey;
    let persistedClient = persistedClients.get(apiKey);
    if (!persistedClient) {
        const httpLink = createHttpLink(APIHelper.getGraphQLUrlByKey(apiKey));
        const restHttpLink = createRestLink(APIHelper.getApiUrlByKey(apiKey));
        const retryLink = createRetryLink();
        // Link chain order: middleware -> errorLink -> authLink -> retryLink -> terminating link
        // - errorLink: Captures all errors for logging/reporting
        // - authLink: Adds authentication headers to each request
        // - retryLink: Retries network failures (positioned after auth to avoid retrying auth failures)
        const commonHandler = [middleWare, createErrorLink(onError), createAuthLink(APIHelper), retryLink];
        const link = ApolloLink.from(commonHandler.concat(httpLink).filter(hasValue));
        const restLink = ApolloLink.from(commonHandler.concat(restHttpLink).filter(hasValue));
        persistedClient = new ApolloClient({
            // Kudos: https://stackoverflow.com/questions/69629051/using-multiple-endpoints-in-apollo-client
            link: ApolloLink.split(operation => {
                return operation.getContext().clientName === REST_GRAPHQL_CLIENTNAME;
            }, restLink, link),
            cache: new InMemoryCache({
                // Sized above the working set: a few-hundred-site org's settings page reads ~57,000
                // selection sets, and under the 50,000 default each broadcast cost 36-627ms instead of ~1ms.
                resultCacheMaxSize: 200000,
                possibleTypes: {
                    SynchronizationStatus: ['Synced', 'SyncError', 'Syncing', 'NotSynced'],
                    HeadcountInterface: ['HeadcountTag', 'HeadcountGroup'],
                    Vaccination: ['VaccinationGiven', 'VaccinationExempted'],
                    ChildAllergen: ['ChildAllergySystemItem', 'ChildAllergyCustomItem'],
                    InvoicePDF: ['InvoicePDFBase64', 'InvoicePDFFile'],
                    PaymentResponse: ['PaymentResponseCompleted', 'PaymentResponseRequiresAction'],
                    PaymentsAccount: ['StripeAccount'],
                    SubscriptionPackagePricing: [
                        'SubscriptionPackagePricingAnnual',
                        'SubscriptionPackagePricingFree',
                        'SubscriptionPackagePricingMonthly',
                    ],
                    RoleAssignee: ['EmployeeAssignee', 'OrganizationAssignee'],
                    OutstandingSubsidyCellValue: ['OutstandingSubsidyMoneyCell', 'OutstandingSubsidyEventsCell'],
                    PermissionTree: ['PermissionLeaf', 'PermissionNode'],
                    QualificationAssignment: ['Badge', 'Level'],
                    Funding: ['FlatRate', 'PerItem'],
                    Report: ['BuiltInReport', 'QrveyReport'],
                    SetupGuideV5SectionState: [
                        'SetupGuideV5SectionStateCompleted',
                        'SetupGuideV5SectionStateInProgress',
                        'SetupGuideV5SectionStateNotStarted',
                    ],
                    SetupGuideV5Question: [
                        'SetupGuideV5ProfileInfoQuestion',
                        'SetupGuideV5PinCodeQuestion',
                        'SetupGuideV5NurseryLogoQuestion',
                        'SetupGuideV5NurseryInfoQuestion',
                        'SetupGuideV5NurseryProfileQuestion',
                        'SetupGuideV5OpeningHoursQuestion',
                        'SetupGuideV5ChildrenQuestion',
                        'SetupGuideV5AgeGroupsQuestion',
                        'SetupGuideV5RoomsQuestion',
                        'SetupGuideV5ClosingDaysQuestion',
                        'SetupGuideV5RolesQuestion',
                        'SetupGuideV5StaffQuestion',
                        'SetupGuideV5WhoShouldSignChildrenInAndOutQuestion',
                        'SetupGuideV5ShouldParentsAndStaffUseTheSignInKioskQuestion',
                        'SetupGuideV5DoYouWantToUseTheSignInKioskQuestion',
                        'SetupGuideV5HowToSignChildrenAndStaffInQuestion',
                        'SetupGuideV5HowToSignChildrenAndStaffInWithPinQuestion',
                        'SetupGuideV5SetupAddressForLocationBasedSignInQuestion',
                        'SetupGuideV5YourQRCodeIsReadyQuestion',
                        'SetupGuideV5WhatDeviceForYourSignInKioskQuestion',
                        'SetupGuideV5InstructionsForUsingSignInKioskQuestion',
                        'SetupGuideV5CountryQuestion',
                        'SetupGuideV5LearningFrameworksForEnglandQuestion',
                        'SetupGuideV5ReferenceMaterialsForEnglandQuestion',
                        'SetupGuideV5AssessmentsQuestion',
                        'SetupGuideV5LearningFrameworksForWalesQuestion',
                        'SetupGuideV5ReferenceMaterialsForWalesQuestion',
                        'SetupGuideV5StatementBasedTrackingQuestion',
                        'SetupGuideV5LearningFrameworksForScotlandQuestion',
                        'SetupGuideV5ReferenceMaterialsForScotlandQuestion',
                        'SetupGuideV5CanParentsWriteObservationsQuestion',
                        'SetupGuideV5DesignYourOwnAssessmentConfigurationQuestion',
                        'SetupGuideV5BuildYourOwnCurriculumQuestion',
                        'SetupGuideV5SetUpInvoicingBasicsQuestion',
                        'SetupGuideV5InvoiceDeadlineQuestion',
                        'SetupGuideV5InvoiceSendScheduleQuestion',
                        'SetupGuideV5AssignNewBillPayersQuestion',
                        'SetupGuideV5InvoiceSendPeriodQuestion',
                        'SetupGuideV5AddPlansToChildrenQuestion',
                        'SetupGuideV5InvoiceLayoutQuestion',
                        'SetupGuideV5AllowUsersToTranslateMessageAndPostsQuestion',
                        'SetupGuideV5EnableSidekickQuestion',
                        'SetupGuideV5WhoShouldSeeSeenByReceiptsQuestion',
                        'SetupGuideV5HowShouldParentsInfoBeDisplayedOnNewsfeedQuestion',
                        'SetupGuideV5ShouldParentsBeAbleToSendMessagesToOtherParentsQuestion',
                        'SetupGuideV5CanParentsDownloadNewsfeedPhotosFromTheAppQuestion',
                        'SetupGuideV5WhenShouldYourStaffBeAutomaticallyLoggedOutQuestion',
                    ],
                    SetupGuideV5WhatsThis: [
                        'SetupGuideV5WhatsThisWithoutExperts',
                        'SetupGuideV5WhatsThisWithMedia',
                        'SetupGuideV5WhatsThisWithExperts',
                    ],
                    SignupV8Question: [
                        'SignupV8QuestionsChooseYourPassword',
                        'SignupV8QuestionsWhatsYourRole',
                        'SignupV8QuestionsWhatIsYourLicensedCapacity',
                        'SignupV8QuestionsHowManySitesInGroup',
                        'SignupV8QuestionsWhatPreviouslyUsedChildCareSystems',
                        'SignupV8QuestionsWhatAreYourExpectations',
                        'SignupV8QuestionsWhatTypeOfSite',
                        'SignupV8QuestionsTestChild',
                    ],
                    Video: ['TranscodingVideo', 'TranscodedVideo'],
                    DeviceStatus: [
                        'DeviceEnrolledInUserContext',
                        'DeviceEnrolledElsewhere',
                        'DeviceNotEnrolled',
                        'DeviceEnrollmentUnknown',
                    ],
                    BillPayerTagInterface: ['BillPayerTagCustom', 'BillPayerTagSystem'],
                    TaskTemplateResult: [
                        'TaskTemplateCareContractSigningResult',
                        'TaskTemplateChildProfileCompletionResult',
                        'TaskTemplateFormSubmissionResult',
                        'TaskTemplateSendMessageResult',
                    ],
                    // Commented out since Apollo figures this out by itself,
                    // EDIT: We still need to do this for interfaces, but apollo will automagically figure it out with unions
                },
                typePolicies: { ...typePoliciesWithMergeStrategies, ...additionalTypePolicies },
                dataIdFromObject: object => {
                    var _a;
                    const fallbackId = defaultDataIdFromObject(object);
                    // `@rest(...)` calls that omit `type` get a synthetic, `AUTOMATIC_TYPENAME_PREFIX`-
                    // prefixed `__typename` injected at every object node by RestLink. We deliberately do
                    // not normalize these into their own cache entries: REST responses frequently carry an
                    // `id` field, and the synthetic names are derived from the query's field path rather
                    // than from a real global type, so two unrelated REST endpoints could otherwise collide
                    // on `${syntheticType}:${id}` — last write wins. Returning `false` keeps every synthetic
                    // REST object embedded inline within its query result (we don't rely on cache
                    // normalization for REST calls), matching how the explicit-`type` REST flow is used.
                    if (typeof object.__typename === 'string' &&
                        object.__typename.startsWith(AUTOMATIC_TYPENAME_PREFIX)) {
                        return false;
                    }
                    // Every type implementing the deprecated `Behavior` interface (the `Behavior*`
                    // types) identifies itself by its global `behaviorId` (e.g. `CanLinkToFrameworks`,
                    // `ObservationVariantAmbiguity`, `CanSeeVariants`). Across the frontend these are
                    // almost always selected as `behaviors { id: behaviorId ... }`, which aliases the
                    // global behavior id onto `id` and makes Apollo normalize them by `__typename:id`.
                    // Because the same behavior id repeats under every parent (every observation, every
                    // site, …), distinct behaviors that carry per-parent payload (e.g. `variants`,
                    // `frameworks`, `version`) collapse into a single cache entry — last write wins —
                    // and every parent reads back the same payload. This is the same class of bug as
                    // the `PlanBehavior` / `RolePermission` / `PayableSource` fixes, but it spans a
                    // whole interface family rather than a single type, so we guard it here by name:
                    // returning `false` keeps every `Behavior*` embedded inline within its parent
                    // instead of globally normalized. They are only ever read off their parent's
                    // `behaviors` list (no `cache.identify` / ref lookups), so this is safe and also
                    // covers any future `Behavior*` type automatically.
                    if (typeof object.__typename === 'string' && object.__typename.startsWith('Behavior')) {
                        return false;
                    }
                    switch (object.__typename) {
                        case 'Group': {
                            const objectCandidate = object;
                            const id = (objectCandidate === null || objectCandidate === void 0 ? void 0 : objectCandidate.groupId) || (objectCandidate === null || objectCandidate === void 0 ? void 0 : objectCandidate.id);
                            if (!hasValue(id)) {
                                return fallbackId;
                            }
                            return `Group:${id}`;
                        }
                        // We use deviceId instead of id for SignInDevices, which we probably should not.
                        case 'SignInDevice': {
                            const objectCandidate = object;
                            return `SignInDevice:${objectCandidate === null || objectCandidate === void 0 ? void 0 : objectCandidate.deviceId}`;
                        }
                        case 'BillPayerWithBalance': {
                            const objectCandidate = object;
                            const id = (_a = objectCandidate === null || objectCandidate === void 0 ? void 0 : objectCandidate.billPayer) === null || _a === void 0 ? void 0 : _a.billPayerId;
                            if (!hasValue(id)) {
                                return fallbackId;
                            }
                            return `BillPayerWithBalance:${id}`;
                        }
                        case 'SubmittedFormFieldData':
                            // ids are referring to another id in another structure.
                            // this means ids here are used in an n->1 relation, thus can be duplicated
                            // in order to not have issues in the cache, we need to make them unique again
                            return `${object.id}:${object.value}`;
                        default:
                            return fallbackId;
                    }
                },
            }),
        });
        persistedClients.set(apiKey, persistedClient);
    }
    return persistedClient;
};
